import {media, spacing, translucify} from '@nfq/react-grid';
import {m as motion} from 'framer-motion';
import styled from 'styled-components';

import {Headline} from 'UI/components/layout/ultra/Headline';

import {AccordionAnimation} from 'UI/animations/ultra';

import {useAccordion} from './useAccordion';

import type {WithChildren} from 'types/global';

interface ComponentProps {
    /** The title of the accordion. */
    title: string;
}

/**
 * A React component representing an accordion.
 * This component renders an accordion with a toggle button for expanding/collapsing content.
 *
 * @param props          The component props.
 * @param props.children The content to be displayed within the accordion.
 * @param props.title    The title of the accordion.
 * @returns              The rendered accordion component.
 *
 * @example
 * ```tsx
 * <Accordion title="Example Accordion">
 *     Accordion Content Goes Here
 * </Accordion>
 * ```
 */
export const Accordion = ({children, title}: WithChildren<ComponentProps>) => {
    const {contentId, headerId, isOpen, toggleIsOpen} = useAccordion();

    return (
        <Wrapper>
            <Headline $size="sm" as="h2">
                <Toggle
                    aria-controls={contentId}
                    aria-expanded={isOpen}
                    id={headerId}
                    type="button"
                    onClick={toggleIsOpen}
                >
                    {title}
                    <Icon />
                </Toggle>
            </Headline>
            <ContentWrapper
                animate={isOpen ? 'visible' : 'hidden'}
                aria-labelledby={headerId}
                id={contentId}
                initial={false}
                variants={AccordionAnimation}
            >
                <Content>{children}</Content>
            </ContentWrapper>
        </Wrapper>
    );
};

const Wrapper = styled.div`
    border-bottom: 1px solid ${({theme}) => translucify(theme.colors.ultraPageForeground, 30)};
    padding-block-end: ${spacing(10)};

    ${media('lg')} {
        padding-block-end: ${spacing(20)};
    }
`;

const Toggle = styled.button`
    align-items: center;
    background: transparent;
    border: 0 none;
    border-radius: 4px;
    color: #fff;
    cursor: pointer;
    display: flex;
    font: inherit;
    justify-content: space-between;
    margin: 0;
    padding: 0;
    text-transform: inherit;
    transition: opacity 180ms ease-in-out;
    width: 100%;

    &:hover {
        opacity: 0.5;
    }

    &:focus-visible {
        outline: 1px ${({theme}) => theme.colors.ultraFocusColor} solid;
        outline-offset: 8px;
    }
`;

const Icon = styled.span`
    display: block;
    height: 20px;
    position: relative;
    width: 20px;

    &:before, &:after {
        background: #fff;
        content: '';
        display: block;
        height: 2px;
        margin-top: -1px;
        position: absolute;
        top: 50%;
        width: 100%;
        border-radius: 2px;
        transform: rotate(0deg);
        transition: transform 300ms ease-in-out;
    }

    &:after {
        transform: rotate(90deg);
    }

    [aria-expanded="true"] &:before,
    [aria-expanded="true"] &:after {
        transform: rotate(180deg);
    }

    ${media('lg')} {
        height: 36px;
        width: 36px;

        &:before, &:after {
            height: 4px;
            margin-top: -2px;
        }
    }
`;

const ContentWrapper = styled(motion.div)`
    overflow: hidden;
`;

const Content = styled.div`
    padding-block-start: ${spacing(6)};

    ${media('lg')} {
        padding-block: ${spacing(16)};
    }
`;